/**
 * TableGrid Component
 * Editable grid of cells with:
 * - Click to edit cell
 * - Tab navigation between cells
 * - Column headers with type indicator
 * - Row numbers
 * - Selection support
 * - Row/column context menus
 */

import { useState, useRef, useCallback, useEffect, KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  ArrowLeft,
  ArrowRight,
  Trash2,
  Copy,
  Plus,
  Type,
  Hash,
  MoveUp,
  MoveDown,
  ChevronDown,
} from 'lucide-react'
import type { TableHeader, TableRow, TableCell } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface TableGridProps {
  headers: TableHeader[]
  rows: TableRow[]
  hasHeaderRow: boolean
  selectedCell: { rowId: string; columnKey: string } | null
  onSelectCell: (cell: { rowId: string; columnKey: string } | null) => void
  onUpdateCell: (rowId: string, columnKey: string, value: Partial<TableCell>) => void
  onUpdateColumn: (columnKey: string, updates: Partial<TableHeader>) => void
  onDeleteColumn: (columnKey: string) => void
  onMoveColumn: (columnKey: string, direction: 'left' | 'right') => void
  onDeleteRow: (rowId: string) => void
  onMoveRow: (rowId: string, direction: 'up' | 'down') => void
  onInsertRow: (rowId: string, position: 'above' | 'below') => void
  onDuplicateRow: (rowId: string) => void
  disabled?: boolean
  className?: string
}

// ============================================
// Cell Type Icons
// ============================================

const CELL_TYPE_ICONS: Record<TableHeader['dataType'], React.ElementType> = {
  Text: Type,
  Number: Hash,
  Currency: Hash,
  Date: Type,
  Boolean: Type,
}

const CELL_TYPE_LABELS: Record<TableHeader['dataType'], string> = {
  Text: 'Text',
  Number: 'Number',
  Currency: 'Currency',
  Date: 'Date',
  Boolean: 'Boolean',
}

// ============================================
// EditableCell Component
// ============================================

interface EditableCellProps {
  value: TableCell
  dataType: TableHeader['dataType']
  isSelected: boolean
  isEditing: boolean
  onSelect: () => void
  onChange: (value: Partial<TableCell>) => void
  onStartEdit: () => void
  onEndEdit: () => void
  onNavigate: (direction: 'up' | 'down' | 'left' | 'right') => void
  disabled?: boolean
}

function EditableCell({
  value,
  dataType,
  isSelected,
  isEditing,
  onSelect,
  onChange,
  onStartEdit,
  onEndEdit,
  onNavigate,
  disabled = false,
}: EditableCellProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [localValue, setLocalValue] = useState(value.value || '')

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Sync local value with prop
  useEffect(() => {
    setLocalValue(value.value || '')
  }, [value.value])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        onChange({ value: localValue })
        onEndEdit()
        onNavigate('down')
        break
      case 'Tab':
        e.preventDefault()
        onChange({ value: localValue })
        onEndEdit()
        onNavigate(e.shiftKey ? 'left' : 'right')
        break
      case 'Escape':
        setLocalValue(value.value || '')
        onEndEdit()
        break
      case 'ArrowUp':
        if (!isEditing) {
          e.preventDefault()
          onNavigate('up')
        }
        break
      case 'ArrowDown':
        if (!isEditing) {
          e.preventDefault()
          onNavigate('down')
        }
        break
      case 'ArrowLeft':
        if (!isEditing) {
          e.preventDefault()
          onNavigate('left')
        }
        break
      case 'ArrowRight':
        if (!isEditing) {
          e.preventDefault()
          onNavigate('right')
        }
        break
    }
  }

  const handleBlur = () => {
    if (isEditing) {
      onChange({ value: localValue })
      onEndEdit()
    }
  }

  const handleDoubleClick = () => {
    if (!disabled) {
      onStartEdit()
    }
  }

  const handleClick = () => {
    if (!disabled) {
      onSelect()
    }
  }

  // Format display value based on type
  const displayValue = value.displayValue || value.value || ''

  // Determine if cell contains a formula
  const isFormula = value.isFormula || (value.value || '').startsWith('=')

  return (
    <td
      className={cn(
        'relative border-r border-b border-neutral-200 dark:border-neutral-700 p-0',
        'transition-colors cursor-pointer',
        isSelected && !isEditing && 'ring-2 ring-inset ring-blue-500 bg-blue-50 dark:bg-blue-900/20',
        !disabled && 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
      )}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {isEditing ? (
        <input
          ref={inputRef}
          type={dataType === 'Number' || dataType === 'Currency' ? 'number' : 'text'}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          className={cn(
            'w-full h-full px-3 py-2 text-sm bg-white dark:bg-neutral-900',
            'border-0 outline-none focus:ring-0',
            dataType === 'Number' || dataType === 'Currency' ? 'text-right font-mono' : ''
          )}
          step={dataType === 'Currency' ? '0.01' : undefined}
        />
      ) : (
        <div
          className={cn(
            'px-3 py-2 text-sm min-h-[38px]',
            'truncate',
            dataType === 'Number' || dataType === 'Currency' ? 'text-right font-mono' : '',
            isFormula && 'text-purple-600 dark:text-purple-400'
          )}
        >
          {displayValue || <span className="text-neutral-300 dark:text-neutral-600">-</span>}
        </div>
      )}
    </td>
  )
}

// ============================================
// ColumnHeader Component
// ============================================

interface ColumnHeaderProps {
  header: TableHeader
  isFirst: boolean
  isLast: boolean
  onUpdate: (updates: Partial<TableHeader>) => void
  onDelete: () => void
  onMoveLeft: () => void
  onMoveRight: () => void
  disabled?: boolean
}

function ColumnHeader({
  header,
  isFirst,
  isLast,
  onUpdate,
  onDelete,
  onMoveLeft,
  onMoveRight,
  disabled = false,
}: ColumnHeaderProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [localLabel, setLocalLabel] = useState(header.label)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const Icon = CELL_TYPE_ICONS[header.dataType] || Type

  const handleLabelKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onUpdate({ label: localLabel })
      setIsEditing(false)
    } else if (e.key === 'Escape') {
      setLocalLabel(header.label)
      setIsEditing(false)
    }
  }

  const handleLabelBlur = () => {
    onUpdate({ label: localLabel })
    setIsEditing(false)
  }

  return (
    <th
      className={cn(
        'relative border-r border-b-2 border-neutral-200 dark:border-neutral-700',
        'bg-neutral-100 dark:bg-neutral-800 text-left',
        'min-w-[100px]'
      )}
      style={{ width: header.width ? `${header.width}px` : 'auto' }}
    >
      <div className="flex items-center gap-1 px-2 py-2">
        {/* Type Icon */}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex-shrink-0">
                <Icon className="h-3.5 w-3.5 text-neutral-400" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              {CELL_TYPE_LABELS[header.dataType]}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Label */}
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={localLabel}
            onChange={(e) => setLocalLabel(e.target.value)}
            onKeyDown={handleLabelKeyDown}
            onBlur={handleLabelBlur}
            className={cn(
              'flex-1 px-1 py-0.5 text-sm font-medium bg-white dark:bg-neutral-900',
              'border border-blue-500 rounded outline-none'
            )}
          />
        ) : (
          <span
            className="flex-1 text-sm font-medium text-neutral-700 dark:text-neutral-300 truncate cursor-pointer"
            onDoubleClick={() => !disabled && setIsEditing(true)}
          >
            {header.label}
          </span>
        )}

        {/* Column Menu */}
        {!disabled && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => setIsEditing(true)}>
                Rename Column
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Type className="h-4 w-4 mr-2" />
                  Column Type
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => onUpdate({ dataType: 'Text' })}>
                    <Type className="h-4 w-4 mr-2" />
                    Text
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onUpdate({ dataType: 'Number' })}>
                    <Hash className="h-4 w-4 mr-2" />
                    Number
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onUpdate({ dataType: 'Currency' })}>
                    <Hash className="h-4 w-4 mr-2" />
                    Currency
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  Alignment
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onClick={() => onUpdate({ alignment: 'Left' })}>
                    Left
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onUpdate({ alignment: 'Center' })}>
                    Center
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onUpdate({ alignment: 'Right' })}>
                    Right
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onMoveLeft} disabled={isFirst}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Move Left
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onMoveRight} disabled={isLast}>
                <ArrowRight className="h-4 w-4 mr-2" />
                Move Right
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onDelete}
                className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Column
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </th>
  )
}

// ============================================
// RowActions Component
// ============================================

interface RowActionsProps {
  rowIndex: number
  isFirst: boolean
  isLast: boolean
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onInsertAbove: () => void
  onInsertBelow: () => void
  onDuplicate: () => void
  disabled?: boolean
}

function RowActions({
  rowIndex,
  isFirst,
  isLast,
  onDelete,
  onMoveUp,
  onMoveDown,
  onInsertAbove,
  onInsertBelow,
  onDuplicate,
  disabled = false,
}: RowActionsProps) {
  return (
    <td className="w-8 border-r border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-0">
      <div className="flex items-center justify-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
              disabled={disabled}
            >
              <span className="text-xs font-mono">{rowIndex + 1}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuItem onClick={onInsertAbove}>
              <Plus className="h-4 w-4 mr-2" />
              Insert Row Above
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onInsertBelow}>
              <Plus className="h-4 w-4 mr-2" />
              Insert Row Below
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDuplicate}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate Row
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onMoveUp} disabled={isFirst}>
              <MoveUp className="h-4 w-4 mr-2" />
              Move Up
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onMoveDown} disabled={isLast}>
              <MoveDown className="h-4 w-4 mr-2" />
              Move Down
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Row
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </td>
  )
}

// ============================================
// TableGrid Component
// ============================================

export function TableGrid({
  headers,
  rows,
  hasHeaderRow,
  selectedCell,
  onSelectCell,
  onUpdateCell,
  onUpdateColumn,
  onDeleteColumn,
  onMoveColumn,
  onDeleteRow,
  onMoveRow,
  onInsertRow,
  onDuplicateRow,
  disabled = false,
  className,
}: TableGridProps) {
  const [editingCell, setEditingCell] = useState<{ rowId: string; columnKey: string } | null>(null)

  // Handle cell navigation
  const handleNavigate = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      if (!selectedCell) return

      const currentRowIndex = rows.findIndex((r) => r.id === selectedCell.rowId)
      const currentColIndex = headers.findIndex((h) => h.key === selectedCell.columnKey)

      let newRowIndex = currentRowIndex
      let newColIndex = currentColIndex

      switch (direction) {
        case 'up':
          newRowIndex = Math.max(0, currentRowIndex - 1)
          break
        case 'down':
          newRowIndex = Math.min(rows.length - 1, currentRowIndex + 1)
          break
        case 'left':
          newColIndex = Math.max(0, currentColIndex - 1)
          break
        case 'right':
          newColIndex = Math.min(headers.length - 1, currentColIndex + 1)
          break
      }

      const newRow = rows[newRowIndex]
      const newHeader = headers[newColIndex]
      if (newRow && newHeader) {
        onSelectCell({ rowId: newRow.id, columnKey: newHeader.key })
      }
    },
    [selectedCell, rows, headers, onSelectCell]
  )

  // Handle keyboard navigation at grid level
  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!selectedCell || editingCell) return

      switch (e.key) {
        case 'Enter':
          setEditingCell(selectedCell)
          break
        case 'ArrowUp':
          e.preventDefault()
          handleNavigate('up')
          break
        case 'ArrowDown':
          e.preventDefault()
          handleNavigate('down')
          break
        case 'ArrowLeft':
          e.preventDefault()
          handleNavigate('left')
          break
        case 'ArrowRight':
          e.preventDefault()
          handleNavigate('right')
          break
        case 'Tab':
          e.preventDefault()
          handleNavigate(e.shiftKey ? 'left' : 'right')
          break
      }
    },
    [selectedCell, editingCell, handleNavigate]
  )

  return (
    <div
      className={cn('overflow-auto max-h-[500px]', className)}
      tabIndex={0}
      onKeyDown={handleGridKeyDown}
    >
      <table className="w-full border-collapse">
        {/* Header Row */}
        {hasHeaderRow && (
          <thead>
            <tr>
              {/* Row number column header */}
              <th className="w-8 border-r border-b-2 border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-800">
                <span className="sr-only">Row</span>
              </th>
              {headers.map((header, headerIndex) => (
                <ColumnHeader
                  key={header.key}
                  header={header}
                  isFirst={headerIndex === 0}
                  isLast={headerIndex === headers.length - 1}
                  onUpdate={(updates) => onUpdateColumn(header.key, updates)}
                  onDelete={() => onDeleteColumn(header.key)}
                  onMoveLeft={() => onMoveColumn(header.key, 'left')}
                  onMoveRight={() => onMoveColumn(header.key, 'right')}
                  disabled={disabled}
                />
              ))}
            </tr>
          </thead>
        )}

        {/* Body Rows */}
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={row.id}>
              {/* Row number / actions */}
              <RowActions
                rowIndex={rowIndex}
                isFirst={rowIndex === 0}
                isLast={rowIndex === rows.length - 1}
                onDelete={() => onDeleteRow(row.id)}
                onMoveUp={() => onMoveRow(row.id, 'up')}
                onMoveDown={() => onMoveRow(row.id, 'down')}
                onInsertAbove={() => onInsertRow(row.id, 'above')}
                onInsertBelow={() => onInsertRow(row.id, 'below')}
                onDuplicate={() => onDuplicateRow(row.id)}
                disabled={disabled}
              />

              {/* Data Cells */}
              {headers.map((header) => {
                const cellKey = `${row.id}-${header.key}`
                const cell = row.cells[header.key] || { value: '' }
                const isSelected =
                  selectedCell?.rowId === row.id && selectedCell?.columnKey === header.key
                const isEditing =
                  editingCell?.rowId === row.id && editingCell?.columnKey === header.key

                return (
                  <EditableCell
                    key={cellKey}
                    value={cell}
                    dataType={header.dataType}
                    isSelected={isSelected}
                    isEditing={isEditing}
                    onSelect={() => onSelectCell({ rowId: row.id, columnKey: header.key })}
                    onChange={(value) => onUpdateCell(row.id, header.key, value)}
                    onStartEdit={() => setEditingCell({ rowId: row.id, columnKey: header.key })}
                    onEndEdit={() => setEditingCell(null)}
                    onNavigate={handleNavigate}
                    disabled={disabled}
                  />
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default TableGrid
